// Global Instructions Rule Applied!
//
// Configuration → Pipeline Stages: the shared stage library
// (public.pipeline_stages), managed in one place.
//
// WHY a panel of its own: a stage row is shared across pipelines, but until
// now it could only be reached through one pipeline's detail panel — which
// shows neither who else uses it nor how many deals sit on it. Renaming a
// stage or moving its decision status from there was an edit with invisible
// blast radius. This panel makes the sharing visible; the detail panel opens
// the same StageEditModal, so both routes behave identically.
//
// Chrome copied from PipelineStatusesPanel (the newest usage-count + guarded-
// delete sibling): a small list, so a type filter and a search box replace
// pagination.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Popconfirm, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type { StageLibraryModel } from '../../../state_manager/data_models/stage_library_model.ts'
import { fetchStageLibrary, deleteStage } from '../services/pipelinesConfigService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'
import StageEditModal from './StageEditModal.tsx'

const { Title, Text } = Typography

const ALL = '__all'

function ColourDot({ colour }: { colour: string | null | undefined }): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: colour || '#D1D5DB',
        border: '0.5px solid rgba(0,0,0,0.15)',
        verticalAlign: 'middle'
      }}
    />
  )
}

export default function PipelineStagesPanel(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [rows, setRows] = useState<StageLibraryModel[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>(ALL)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<StageLibraryModel | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchStageLibrary(token())
      if (result.error) {
        showErrorToast(result.error)
        return
      }
      setRows(result.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Type filter options come from the data, so a pipeline kind with no stages
  // never shows an empty tab.
  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>()
    rows.forEach((r) => {
      const label = (r.pipeline_stages__type__label as string) || 'Untyped'
      seen.set(label, label)
    })
    return [{ value: ALL, label: 'All' }, ...Array.from(seen.keys()).sort().map((l) => ({ value: l, label: l }))]
  }, [rows])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      const label = (r.pipeline_stages__type__label as string) || 'Untyped'
      if (typeFilter !== ALL && label !== typeFilter) return false
      if (!q) return true
      return ((r.pipeline_stages__name as string) || '').toLowerCase().includes(q)
    })
  }, [rows, search, typeFilter])

  const openCreate = (): void => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (row: StageLibraryModel): void => {
    setEditing(row)
    setModalOpen(true)
  }

  const onDelete = async (row: StageLibraryModel): Promise<void> => {
    setDeletingId(row.pipeline_stages__id as string)
    try {
      const result = await deleteStage(row.pipeline_stages__id as string, token())
      if (result.error) {
        // The backend 409s if the stage was attached between render and click.
        showErrorToast(result.error)
        return
      }
      toast.success('Stage deleted')
      await load()
      refreshBoardConfigs()
    } finally {
      setDeletingId(null)
    }
  }

  const columns: ColumnsType<StageLibraryModel> = [
    {
      title: 'Stage',
      dataIndex: 'pipeline_stages__name',
      render: (name: string, row) => (
        <Space size='small'>
          <ColourDot colour={row.pipeline_stages__colour as string} />
          <span>{name || '—'}</span>
        </Space>
      )
    },
    {
      title: 'Pipeline type',
      dataIndex: 'pipeline_stages__type__label',
      width: 180,
      render: (v: string) => v || <Text type='secondary'>—</Text>
    },
    {
      title: 'Decision status',
      dataIndex: 'pipeline_stages__decision_status__label',
      width: 180,
      render: (v: string) => (v ? <Tag color='blue'>{v}</Tag> : <Text type='secondary'>—</Text>)
    },
    {
      title: 'Used by',
      key: 'used_by',
      render: (_, row) => {
        const used = (row.pipeline_stages__used_by as { pipelines__name?: string | null }[]) || []
        if (used.length === 0) return <Text type='secondary'>unused</Text>
        return <span>{used.map((p) => p.pipelines__name).join(', ')}</span>
      }
    },
    {
      title: 'Deals',
      dataIndex: 'pipeline_stages__deal_count',
      width: 90,
      align: 'right' as const,
      render: (v: number) => (v ?? 0).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, row) => {
        const inUse = ((row.pipeline_stages__usage_count as number) ?? 0) > 0
        return (
          <Space size='small'>
            <Tooltip title='Edit'>
              <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openEdit(row)} />
            </Tooltip>
            {inUse ? (
              <Tooltip title='In use by a pipeline — remove it there first'>
                <Button type='text' size='small' danger disabled icon={<DeleteOutlined />} />
              </Tooltip>
            ) : (
              <Popconfirm title='Delete this stage?' okText='Delete' onConfirm={() => onDelete(row)}>
                <Button
                  type='text'
                  size='small'
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === row.pipeline_stages__id}
                />
              </Popconfirm>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>
          Pipeline Stages
        </Title>
        <Space>
          <Input.Search
            allowClear
            placeholder='Search stages'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          <Segmented value={typeFilter} onChange={(v) => setTypeFilter(v as string)} options={typeOptions} />
          <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
            New Stage
          </Button>
        </Space>
      </div>

      <Text type='secondary' className='mb-2'>
        Stages are shared: attaching one to a second pipeline reuses the same stage, so an edit here
        applies everywhere it is used. Attach and order stages in Configuration → Pipelines.
      </Text>

      <Table
        size='small'
        rowKey='pipeline_stages__id'
        loading={loading}
        pagination={false}
        dataSource={visible}
        columns={columns}
      />

      <StageEditModal
        open={modalOpen}
        stage={editing}
        onClose={() => setModalOpen(false)}
        onSaved={async () => {
          await load()
          refreshBoardConfigs()
        }}
      />
    </div>
  )
}
